import { Loader2 } from "lucide-react";
import type React from "react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { AuthProvider, useAuth } from "./hooks/useAuth.js";
import { type Role, getAllowedBoards } from "./lib/api.js";
import { ComingSoon } from "./pages/ComingSoon.js";
import { FkDashboard } from "./pages/FkDashboard.js";
import { KpiReporting } from "./pages/KpiReporting.js";
import { Login } from "./pages/Login.js";
import { MeinTeam } from "./pages/MeinTeam.js";
import { ModelValidation } from "./pages/ModelValidation.js";
import { Notifications } from "./pages/Notifications.js";
import { OnboardingSuccess } from "./pages/OnboardingSuccess.js";
import { PersonalleiterCockpit } from "./pages/PersonalleiterCockpit.js";
import { Pulsumfrage } from "./pages/Pulsumfrage.js";
import { RetentionRadar } from "./pages/RetentionRadar.js";
import { ScenarioPlanning } from "./pages/ScenarioPlanning.js";
import { TenureAnalysis } from "./pages/TenureAnalysis.js";
import { UserAdmin } from "./pages/UserAdmin.js";
import { Wirtschaftlichkeit } from "./pages/Wirtschaftlichkeit.js";
import { Cockpit } from "./pages/cockpit/Cockpit.js";

/**
 * Unübersehbarer Marker für die lokale Entwicklungs-Instanz. Rendert nur im
 * Vite-Dev-Modus (import.meta.env.DEV) — im Production-Build der deployten
 * Version (hr-analytics.mindsquare.de) existiert er nicht. Zweck: lokale
 * Vorschau und Live-System auf einen Blick unterscheidbar machen.
 */
function DevPreviewBanner() {
  useEffect(() => {
    document.title = "🧪 VORSCHAU — HR Analytics (lokal)";
  }, []);
  return (
    <div
      className="flex items-center justify-center gap-2 px-4 py-1.5 text-xs font-bold tracking-wide shrink-0"
      style={{ background: "#7c3aed", color: "#fff" }}
    >
      🧪 LOKALE VORSCHAU — nicht das Live-System (hr-analytics.mindsquare.de)
    </div>
  );
}

function FullscreenLoader() {
  return (
    <div
      className="min-h-screen flex items-center justify-center"
      style={{ background: "var(--bg-base)" }}
    >
      <Loader2 className="animate-spin" size={20} style={{ color: "var(--accent)" }} />
    </div>
  );
}

// Board → Landing-Pfad, in Prioritätsreihenfolge. Das erste erlaubte Board ist
// das Startziel für Rollen ohne Dashboard-Zugriff (z. B. kpi_viewer → KPI).
const BOARD_LANDING: ReadonlyArray<readonly [string, string]> = [
  ["dashboard", "/"],
  ["kpi-reporting", "/kpi-reporting"],
  ["pulsumfrage", "/pulsumfrage"],
  ["fk-dashboard", "/fk-dashboard"],
  ["mein-team", "/mein-team"],
  ["onboarding", "/onboarding"],
  ["retention", "/retention"],
  ["personalleiter-cockpit", "/personalleiter"],
  ["wirtschaftlichkeit", "/wirtschaftlichkeit"],
  ["tenure", "/tenure"],
  ["scenario", "/scenario"],
  ["notifications", "/notifications"],
  ["model-validation", "/model-validation"],
  ["user-admin", "/admin"],
];

/**
 * Startseite "/": zeigt das Dashboard nur, wenn die Rolle das Board darf. Sonst
 * (z. B. kpi_viewer) leitet sie auf das erste erlaubte Board um — damit landet
 * niemand auf einem Board, das er laut Berechtigung nicht sehen soll.
 */
function HomeRoute() {
  const [allowed, setAllowed] = useState<Set<string> | null>(null);
  useEffect(() => {
    getAllowedBoards()
      .then((b) => setAllowed(new Set(b)))
      .catch(() => setAllowed(new Set()));
  }, []);
  if (allowed === null) return <FullscreenLoader />;
  if (allowed.has("dashboard")) return <Cockpit />;
  const first = BOARD_LANDING.find(([b]) => b !== "dashboard" && allowed.has(b));
  if (first) return <Navigate to={first[1]} replace />;
  return (
    <div
      className="min-h-screen flex items-center justify-center px-6 text-center"
      style={{ background: "var(--bg-base)", color: "var(--text-muted)" }}
    >
      Kein Board freigeschaltet. Bitte an einen Administrator wenden.
    </div>
  );
}

function PrivateRoute({
  element,
  requireRole,
}: { element: React.ReactElement; requireRole?: Role }) {
  const { user, loading } = useAuth();
  if (loading) return <FullscreenLoader />;
  if (!user) return <Navigate to="/login" replace />;
  if (requireRole) {
    // kpi_viewer steht außerhalb der Linie (nur KPI-Board) → wie viewer (1),
    // damit es keine höher-gestuften Boards laden kann.
    const order = { viewer: 1, kpi_viewer: 1, manager: 2, vorstand: 2, hrbp: 3, admin: 4 } as const;
    if (order[user.role] < order[requireRole]) return <Navigate to="/" replace />;
  }
  return element;
}

export function App() {
  const { t } = useTranslation();
  return (
    <AuthProvider>
      <BrowserRouter>
        {/* Flex-Spalte, damit der Dev-Banner die App-Höhe verkleinert statt
            Inhalt zu überdecken; ohne Banner füllt flex-1 wie bisher alles. */}
        <div className="h-full flex flex-col">
          {import.meta.env.DEV && <DevPreviewBanner />}
          <div className="flex-1 min-h-0 overflow-auto">
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/" element={<PrivateRoute element={<HomeRoute />} />} />
              <Route
                path="/onboarding"
                element={<PrivateRoute element={<OnboardingSuccess />} />}
              />
              <Route path="/retention" element={<PrivateRoute element={<RetentionRadar />} />} />
              <Route path="/manager" element={<Navigate to="/" replace />} />
              <Route path="/fk-dashboard" element={<PrivateRoute element={<FkDashboard />} />} />
              {/* Kein requireRole: Zugriff steuert die Board-Berechtigung im Backend
              (admin/hrbp/manager); Sidebar blendet es für andere aus. */}
              <Route path="/mein-team" element={<PrivateRoute element={<MeinTeam />} />} />
              <Route
                path="/personalleiter"
                element={<PrivateRoute element={<PersonalleiterCockpit />} />}
              />
              {/* „Projektumsatz" ist in der Wirtschaftlichkeit aufgegangen
                  (Entscheidung 12.08., eine Umsatz-Wahrheit). Der alte Pfad
                  bleibt als Umleitung, damit Lesezeichen nicht ins Leere laufen. */}
              <Route path="/billing" element={<Navigate to="/wirtschaftlichkeit" replace />} />
              <Route
                path="/wirtschaftlichkeit"
                element={<PrivateRoute element={<Wirtschaftlichkeit />} />}
              />
              <Route path="/scenario" element={<PrivateRoute element={<ScenarioPlanning />} />} />
              <Route path="/tenure" element={<PrivateRoute element={<TenureAnalysis />} />} />
              <Route path="/notifications" element={<PrivateRoute element={<Notifications />} />} />
              <Route
                path="/model-validation"
                element={<PrivateRoute element={<ModelValidation />} />}
              />
              <Route
                path="/assistant"
                element={<PrivateRoute element={<ComingSoon title={t("nav.assistant")} />} />}
              />
              {/* Kein requireRole: Zugriff steuert die Board-Berechtigung im Backend
              (manager/hrbp/admin/kpi_viewer); Sidebar blendet es für andere aus. */}
              <Route path="/kpi-reporting" element={<PrivateRoute element={<KpiReporting />} />} />
              <Route
                path="/pulsumfrage"
                element={<PrivateRoute requireRole="manager" element={<Pulsumfrage />} />}
              />
              <Route
                path="/admin"
                element={<PrivateRoute requireRole="admin" element={<UserAdmin />} />}
              />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </div>
        </div>
      </BrowserRouter>
    </AuthProvider>
  );
}
